import type { History, Location } from 'history'

export const DIRECTORY_SORT_VALUES = [
  'az',
  'mostReviewed',
  'newest',
  'leastReviewed',
  'highRating',
  'lowRating',
  'za',
] as const

export const DEFAULT_DIRECTORY_SORT = 'highRating'

export type DirectoryTab = 'hospitals' | 'agencies'

export type DirectorySearchParams = {
  q: string
  sort: string
  page: number
  tab: DirectoryTab
}

export type DirectoryReturnLocation = {
  pathname: string
  search: string
  hash: string
}

const DIRECTORY_SCROLL_STORAGE_KEY = 'scrubstr:directory-scroll-y'
const DIRECTORY_SCROLL_RESTORE_FLAG = 'scrubstr:directory-restore-scroll'

function normalizeHash(hash: string): string {
  return hash.startsWith('#') ? hash.slice(1) : hash
}

function isValidSort(sort: string | null): sort is string {
  return Boolean(sort && DIRECTORY_SORT_VALUES.includes(sort as (typeof DIRECTORY_SORT_VALUES)[number]))
}

export function parseDirectorySearchFromLocation(location: Pick<Location, 'search' | 'hash'>): DirectorySearchParams {
  const searchParams = new URLSearchParams(location.search)
  const q = searchParams.get('q') ?? ''
  const sortParam = searchParams.get('sort')
  const sort = isValidSort(sortParam) ? sortParam : DEFAULT_DIRECTORY_SORT
  const pageParam = Number.parseInt(searchParams.get('page') ?? '1', 10)
  const page = Number.isFinite(pageParam) && pageParam >= 1 ? pageParam : 1
  const tab: DirectoryTab = normalizeHash(location.hash).includes('tab=agencies') ? 'agencies' : 'hospitals'

  return { page, q, sort, tab }
}

export function buildDirectorySearchQuery(params: Pick<DirectorySearchParams, 'q' | 'sort' | 'page'>): string {
  const searchParams = new URLSearchParams()

  if (params.q.trim()) {
    searchParams.set('q', params.q.trim())
  }
  if (params.sort !== DEFAULT_DIRECTORY_SORT) {
    searchParams.set('sort', params.sort)
  }
  if (params.page > 1) {
    searchParams.set('page', String(params.page))
  }

  const query = searchParams.toString()
  return query ? `?${query}` : ''
}

export function buildDirectorySearchHash(tab: DirectoryTab): string {
  return tab === 'agencies' ? 'tab=agencies' : ''
}

export function buildDirectorySearchLocation(params: DirectorySearchParams): DirectoryReturnLocation {
  return {
    hash: buildDirectorySearchHash(params.tab),
    pathname: '/hospitals',
    search: buildDirectorySearchQuery(params),
  }
}

export function directorySearchLocationMatches(
  location: Pick<Location, 'pathname' | 'search' | 'hash'>,
  desired: DirectoryReturnLocation,
): boolean {
  return (
    location.pathname === desired.pathname &&
    (location.search || '') === desired.search &&
    normalizeHash(location.hash || '') === desired.hash
  )
}

export function saveDirectoryScrollPosition(): void {
  sessionStorage.setItem(DIRECTORY_SCROLL_STORAGE_KEY, String(window.scrollY))
  sessionStorage.setItem(DIRECTORY_SCROLL_RESTORE_FLAG, '1')
}

export function consumeDirectoryScrollRestore(): number | null {
  if (sessionStorage.getItem(DIRECTORY_SCROLL_RESTORE_FLAG) !== '1') {
    return null
  }

  sessionStorage.removeItem(DIRECTORY_SCROLL_RESTORE_FLAG)
  const raw = sessionStorage.getItem(DIRECTORY_SCROLL_STORAGE_KEY)
  sessionStorage.removeItem(DIRECTORY_SCROLL_STORAGE_KEY)

  if (!raw) {
    return null
  }

  const scrollY = Number(raw)
  return Number.isFinite(scrollY) ? scrollY : null
}

export function getDirectoryDetailLinkState(directoryReturnTo: DirectoryReturnLocation) {
  return { directoryReturnTo }
}

export function navigateBackToDirectory(history: History, location: Location): void {
  const returnTo = (location.state as { directoryReturnTo?: DirectoryReturnLocation } | undefined)?.directoryReturnTo

  if (returnTo) {
    history.push(returnTo)
    return
  }

  if (history.length > 1) {
    history.goBack()
    return
  }

  history.push(buildDirectorySearchLocation({ page: 1, q: '', sort: DEFAULT_DIRECTORY_SORT, tab: 'hospitals' }))
}
